import type { SDKModule, SDKContext, PostalAddress } from '../types';
import { log } from '../utils/logger';

/**
 * Postal Code Auto-Fill Module (Japan)
 *
 * Watches inputs marked with data-postal-role="code" within a data-postal-group.
 * On a complete 7-digit entry, fetches /api/postal/{code} and fills sibling
 * fields by data-postal-role (prefecture, city, town, street).
 *
 * Behavior is identical in preview — auto-fill is a pure UX helper with no
 * analytics side-effects, so it stays on so editors can validate forms.
 */
export function createPostalAutoFill(): SDKModule {
  let ctx: SDKContext;
  let handler: ((e: Event) => void) | null = null;
  const cache: Record<string, PostalAddress[]> = {};

  function fillFields(group: string, addresses: PostalAddress[]): void {
    const addr = addresses[0];
    if (!addr) return;

    const roleMap: Record<string, string> = {
      prefecture: addr.prefecture || '',
      city: addr.city || '',
      town: addr.town || '',
    };

    const fields = document.querySelectorAll<HTMLElement>(`[data-postal-group="${group}"]`);
    for (const field of fields) {
      const role = field.getAttribute('data-postal-role');
      if (!role || role === 'code') continue;

      const value = roleMap[role] || '';
      if (!value) continue;

      if (field instanceof HTMLSelectElement) {
        let hasOption = false;
        for (const opt of field.options) {
          if (opt.value === value) {
            hasOption = true;
            break;
          }
        }
        if (!hasOption) {
          const opt = document.createElement('option');
          opt.value = value;
          opt.textContent = value;
          field.appendChild(opt);
        }
        field.value = value;
        field.disabled = false;
        field.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (field instanceof HTMLInputElement) {
        field.value = value;
        field.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    const streetField = document.querySelector<HTMLElement>(
      `[data-postal-group="${group}"][data-postal-role="street"]`,
    );
    if (streetField && streetField instanceof HTMLInputElement) {
      streetField.focus();
    }
  }

  function handleInput(e: Event): void {
    const input = e.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (input.getAttribute('data-postal-role') !== 'code') return;

    const code = (input.value || '').replace(/[^0-9]/g, '');
    if (code.length !== 7) return;

    const group = input.getAttribute('data-postal-group');
    if (!group) return;

    if (cache[code]) {
      fillFields(group, cache[code]);
      return;
    }

    fetch(ctx.endpoint + '/api/postal/' + code)
      .then((res) => res.json())
      .then((json: { data?: { addresses?: PostalAddress[] } }) => {
        if (json?.data?.addresses && json.data.addresses.length > 0) {
          cache[code] = json.data.addresses;
          fillFields(group, json.data.addresses);
        }
      })
      .catch((err) => {
        log('warn', 'postal lookup failed for ' + code, err);
      });
  }

  return {
    name: 'postal-autofill',
    init(context: SDKContext): void {
      ctx = context;
      handler = handleInput;
      document.addEventListener('input', handler, true);
    },
    destroy(): void {
      if (handler) {
        document.removeEventListener('input', handler, true);
        handler = null;
      }
    },
  };
}
